import {
  type ContentCatalog,
  findCatalogLevel,
  type LevelSummary,
  MAX_STARS,
  type ProgressState,
  type WorldSummary,
} from '@md/shared';

/**
 * Règles de déblocage (concept §4.3) — fonctions PURES : elles ne lisent ni
 * n'écrivent aucun état, elles combinent un `ContentCatalog` (structure du
 * jeu) et un `ProgressState` (avancement du joueur) pour répondre à « qu'est-
 * ce qui est jouable ? ».
 *
 * Règles :
 *  - le chapitre 1 d'un world est toujours débloqué ; un chapitre C+1 l'est
 *    quand TOUS les niveaux du chapitre C sont terminés (≥ 1 étoile) ;
 *  - dans un chapitre débloqué, le niveau 1 est toujours jouable, et le
 *    niveau i > 0 l'est quand le niveau i-1 est terminé ;
 *  - les niveaux `status: 'draft'` (soumis via le MCP, non publiés) ET les
 *    niveaux non « seed » (publiés après coup, `seeded: false`) sont toujours
 *    débloqués et ne comptent ni dans la chaîne de déblocage des niveaux
 *    voisins, ni dans le décompte d'étoiles/complétion du chapitre : la
 *    progression est celle du world LIVRÉ avec le jeu. Un testeur ne doit
 *    jamais être bloqué par un brouillon, et du contenu ajouté après coup ne
 *    doit pas faire progresser le joueur à sa place.
 */

/** État de déblocage d'un niveau au sein de son chapitre. */
export interface LevelUnlockState {
  readonly levelId: string;
  readonly unlocked: boolean;
  readonly completed: boolean;
  readonly stars: number;
}

/** État de déblocage d'un chapitre, avec le détail de ses niveaux. */
export interface ChapterUnlockState {
  readonly world: string;
  readonly chapter: number;
  readonly unlocked: boolean;
  readonly completed: boolean;
  /** Étoiles gagnées dans le chapitre (niveaux publiés uniquement). */
  readonly stars: number;
  /** `MAX_STARS × nombre de niveaux publiés` du chapitre. */
  readonly maxStars: number;
  readonly levels: readonly LevelUnlockState[];
}

interface LevelLocation {
  readonly world: WorldSummary;
  readonly level: LevelSummary;
}

/** Retrouve un niveau et le world qui le contient. */
function locateLevel(catalog: ContentCatalog, levelId: string): LevelLocation | null {
  const level = findCatalogLevel(catalog, levelId);
  if (!level) {
    return null;
  }
  const world = catalog.worlds.find((entry) => entry.id === level.world);
  return world ? { world, level } : null;
}

/** Tri stable par numéro de niveau (`LevelSummary.level`, spec §7). */
function sortLevels(levels: readonly LevelSummary[]): LevelSummary[] {
  return [...levels].sort((a, b) => a.level - b.level);
}

/** Tri stable par numéro de chapitre. */
function sortChapters<T extends { chapter: number }>(chapters: readonly T[]): T[] {
  return [...chapters].sort((a, b) => a.chapter - b.chapter);
}

/**
 * État de déblocage de tous les chapitres d'un world, dans l'ordre.
 * Renvoie `[]` si le catalogue est absent ou ne contient pas ce world.
 */
export function computeUnlockState(
  catalog: ContentCatalog | null,
  world: string,
  progress: ProgressState,
): readonly ChapterUnlockState[] {
  if (!catalog) {
    return [];
  }
  const worldSummary = catalog.worlds.find((entry) => entry.id === world);
  if (!worldSummary) {
    return [];
  }

  const result: ChapterUnlockState[] = [];
  let previousChapterCompleted = true; // le chapitre 1 est toujours débloqué

  for (const chapter of sortChapters(worldSummary.chapters)) {
    const chapterUnlocked = previousChapterCompleted;
    const levelStates: LevelUnlockState[] = [];

    let previousCountedLevelCompleted = true; // le niveau 1 (compté) est toujours jouable si le chapitre l'est
    let stars = 0;
    let maxStars = 0;
    let allCountedCompleted = true;

    for (const level of sortLevels(chapter.levels)) {
      const stored = progress.levels[level.id];
      const levelStars = stored?.stars ?? 0;
      const completed = levelStars >= 1;

      if (level.status === 'draft' || !level.seeded) {
        // Brouillon ou niveau poussé après coup par le MCP : toujours jouable,
        // hors chaîne de déblocage et hors décompte. La progression est celle
        // du world LIVRÉ avec le jeu ; du contenu ajouté ensuite ne doit ni
        // bloquer un joueur ni progresser à sa place.
        levelStates.push({ levelId: level.id, unlocked: true, completed, stars: levelStars });
        continue;
      }

      const unlocked = chapterUnlocked && previousCountedLevelCompleted;
      levelStates.push({ levelId: level.id, unlocked, completed, stars: levelStars });

      stars += levelStars;
      maxStars += MAX_STARS;
      allCountedCompleted &&= completed;
      previousCountedLevelCompleted = completed;
    }

    result.push({
      world,
      chapter: chapter.chapter,
      unlocked: chapterUnlocked,
      completed: allCountedCompleted,
      stars,
      maxStars,
      levels: levelStates,
    });

    previousChapterCompleted = allCountedCompleted;
  }

  return result;
}

/** Un niveau est-il jouable ? Tolérant à un catalogue absent (renvoie `false`). */
export function isLevelUnlocked(
  catalog: ContentCatalog | null,
  levelId: string,
  progress: ProgressState,
): boolean {
  if (!catalog) {
    return false;
  }
  const located = locateLevel(catalog, levelId);
  if (!located) {
    return false;
  }
  const chapters = computeUnlockState(catalog, located.world.id, progress);
  const chapterState = chapters.find((entry) => entry.chapter === located.level.chapter);
  return chapterState?.levels.find((entry) => entry.levelId === levelId)?.unlocked ?? false;
}

/** Un chapitre est-il débloqué ? Tolérant à un catalogue absent (renvoie `false`). */
export function isChapterUnlocked(
  catalog: ContentCatalog | null,
  world: string,
  chapter: number,
  progress: ProgressState,
): boolean {
  if (!catalog) {
    return false;
  }
  const chapters = computeUnlockState(catalog, world, progress);
  return chapters.find((entry) => entry.chapter === chapter)?.unlocked ?? false;
}

/**
 * Identifiant du prochain niveau à jouer après `levelId` : le suivant dans le
 * même chapitre, sinon le premier du chapitre suivant, sinon `null` (fin du
 * world). Ne dépend pas de la progression : c'est une navigation de
 * catalogue, pas une règle de déblocage.
 */
export function nextLevelId(catalog: ContentCatalog | null, levelId: string): string | null {
  if (!catalog) {
    return null;
  }
  const located = locateLevel(catalog, levelId);
  if (!located) {
    return null;
  }

  const chapters = sortChapters(located.world.chapters);
  const chapterIndex = chapters.findIndex((entry) => entry.chapter === located.level.chapter);
  const currentChapter = chapters[chapterIndex];
  if (!currentChapter) {
    return null;
  }

  const currentLevels = sortLevels(currentChapter.levels);
  const levelIndex = currentLevels.findIndex((entry) => entry.id === levelId);
  const nextInChapter = currentLevels[levelIndex + 1];
  if (nextInChapter) {
    return nextInChapter.id;
  }

  const nextChapter = chapters[chapterIndex + 1];
  if (!nextChapter) {
    return null;
  }
  return sortLevels(nextChapter.levels)[0]?.id ?? null;
}
